import {
  SetNextStepDialog,
  type SetNextStepValues,
} from '@/components/common/waiting-on/SetNextStepDialog';
import { useSetArCollectionThreadNextStep } from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { useCollectionWaitingOnOptions } from './collectionWaitingOnOptions';

/** The slice of a thread the dialog needs — satisfied by both the list item
 * DTO and the detail DTO, so both surfaces share this one control. */
export interface SetNextStepThread {
  id: string;
  referenceCode: string;
  title: string;
  customerName?: string | null;
  waitingOnKey: string | null;
  followUpAt: string | null;
}

/**
 * AR collections' binding of the shared Set Next Step dialog: supplies the
 * org's collection waiting-on options and the collection-thread mutation, and
 * keeps the thread vocabulary (`customerName` renders as the dialog's
 * subtitle) so the AR call sites stay unchanged.
 */
export function CollectionThreadSetNextStepDialog({
  thread,
  onOpenChange,
}: {
  /** The thread being parked; null keeps the dialog closed. */
  thread: SetNextStepThread | null;
  onOpenChange: (open: boolean) => void;
}) {
  const options = useCollectionWaitingOnOptions();
  const setNextStep = useSetArCollectionThreadNextStep();
  const threadId = thread?.id;

  return (
    <SetNextStepDialog
      record={
        thread
          ? {
              id: thread.id,
              referenceCode: thread.referenceCode,
              title: thread.title,
              subtitle: thread.customerName,
              waitingOnKey: thread.waitingOnKey,
              followUpAt: thread.followUpAt,
            }
          : null
      }
      options={options}
      isSaving={setNextStep.isPending}
      onSave={(values: SetNextStepValues) => {
        // Unreachable: the dialog only saves while a thread is open.
        if (threadId === undefined) return Promise.resolve();
        return setNextStep.mutateAsync({ id: threadId, ...values });
      }}
      onOpenChange={onOpenChange}
    />
  );
}
